import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FileSignature, Send, X, CheckCircle2, Clock, AlertTriangle, Download, XCircle, Plus, User, Settings, ShieldCheck, Link2, Mail } from "lucide-react";
import { toast } from "sonner";
import { api } from "../services/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

const STATUS_CFG = {
  draft:     { color: "#f59e0b", bg: "rgba(245,158,11,0.12)",  icon: Clock },
  pending:   { color: "#0ea5e9", bg: "rgba(14,165,233,0.12)",  icon: Clock },
  done:      { color: "#22c55e", bg: "rgba(34,197,94,0.12)",   icon: CheckCircle2 },
  expired:   { color: "#94a3b8", bg: "rgba(148,163,184,0.12)", icon: AlertTriangle },
  cancelled: { color: "#94a3b8", bg: "rgba(148,163,184,0.12)", icon: XCircle },
  rejected:  { color: "#ef4444", bg: "rgba(239,68,68,0.12)",   icon: XCircle },
  error:     { color: "#ef4444", bg: "rgba(239,68,68,0.12)",   icon: AlertTriangle },
};

const EMPTY_SIGNER = { first_name: "", last_name: "", email: "", phone_number: "" };
const statusLabel = (status, t) => t(`eSignatureHub.status.${status}`, { defaultValue: status });

export default function ESignatureHub() {
  const { t } = useTranslation();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  const [form, setForm] = useState({
    title: "",
    message: "",
    signers: [{ ...EMPTY_SIGNER }],
    pdf_base64: null,
    document_filename: "",
  });

  // Per-tenant Yousign settings (admin/RH only — 403 hides the panel)
  const [settings, setSettings] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [settingsForm, setSettingsForm] = useState({ provider: "internal", api_key: "", environment: "sandbox" });
  const [savingSettings, setSavingSettings] = useState(false);
  const [testingSettings, setTestingSettings] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get("v1/esignature/")
      .then((r) => setRequests(r.data || []))
      .catch(() => toast.error(t("eSignatureHub.toasts.loadError")))
      .finally(() => setLoading(false));
  }, [t]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get("v1/esignature/settings")
      .then((r) => {
        setSettings(r.data);
        setSettingsForm((f) => ({ ...f, provider: r.data.provider, environment: r.data.environment }));
      })
      .catch(() => setSettings(null));
  }, []);

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      const payload = { provider: settingsForm.provider, environment: settingsForm.environment };
      if (settingsForm.api_key.trim()) payload.api_key = settingsForm.api_key.trim();
      const r = await api.put("v1/esignature/settings", payload);
      setSettings(r.data);
      setSettingsForm((f) => ({ ...f, api_key: "" }));
      toast.success(t("eSignatureHub.settings.saved"));
    } catch (e) {
      toast.error(e.response?.data?.detail || t("eSignatureHub.toasts.loadError"));
    } finally { setSavingSettings(false); }
  };

  const testSettings = async () => {
    setTestingSettings(true);
    try {
      const r = await api.post("v1/esignature/settings/test");
      toast.success(t("eSignatureHub.settings.testOk", { env: r.data.environment }));
    } catch (e) {
      toast.error(e.response?.data?.detail || t("eSignatureHub.settings.testFail"));
    } finally { setTestingSettings(false); }
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.includes("pdf")) {
      toast.error(t("eSignatureHub.toasts.pdfOnly"));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result.split(",")[1];
      setForm((f) => ({ ...f, pdf_base64: base64, document_filename: file.name }));
    };
    reader.readAsDataURL(file);
  };

  const addSigner = () => {
    if (form.signers.length >= 5) { toast.error(t("eSignatureHub.toasts.maxSigners")); return; }
    setForm((f) => ({ ...f, signers: [...f.signers, { ...EMPTY_SIGNER }] }));
  };

  const removeSigner = (idx) => {
    setForm((f) => ({ ...f, signers: f.signers.filter((_, i) => i !== idx) }));
  };

  const updateSigner = (idx, field, value) => {
    setForm((f) => ({
      ...f,
      signers: f.signers.map((s, i) => i === idx ? { ...s, [field]: value } : s),
    }));
  };

  const handleSend = async () => {
    if (!form.title.trim()) { toast.error(t("eSignatureHub.toasts.titleRequired")); return; }
    if (!form.pdf_base64) { toast.error(t("eSignatureHub.toasts.selectPdf")); return; }
    const invalid = form.signers.find((s) => !s.first_name || !s.last_name || !s.email);
    if (invalid) { toast.error(t("eSignatureHub.toasts.signersRequired")); return; }

    setSubmitting(true);
    try {
      await api.post("v1/esignature/send", {
        title: form.title,
        message: form.message || undefined,
        signers: form.signers.map((s) => ({
          first_name: s.first_name,
          last_name: s.last_name,
          email: s.email,
          phone_number: s.phone_number || undefined,
        })),
        pdf_base64: form.pdf_base64,
        document_filename: form.document_filename,
      });
      toast.success(t("eSignatureHub.toasts.sent"));
      setShowForm(false);
      setForm({ title: "", message: "", signers: [{ ...EMPTY_SIGNER }], pdf_base64: null, document_filename: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("eSignatureHub.toasts.sendError"));
      load();
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = async (id) => {
    if (!window.confirm(t("eSignatureHub.confirmCancel"))) return;
    try {
      await api.post(`v1/esignature/${id}/cancel`);
      toast.success(t("eSignatureHub.toasts.cancelled"));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("eSignatureHub.toasts.genericError"));
    }
  };

  const handleDownload = async (req) => {
    try {
      const resp = await api.get(`v1/esignature/${req.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = (req.document_filename || "signed.pdf").replace(".pdf", "-signed.pdf");
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(t("eSignatureHub.toasts.downloadError"));
    }
  };

  const stats = {
    pending: requests.filter((r) => r.status === "pending").length,
    done: requests.filter((r) => r.status === "done").length,
    total: requests.length,
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-semibold" style={{ color: "var(--lm-ink)", letterSpacing: "-0.5px" }}>
            {t("eSignatureHub.title")}
          </h2>
          <p className="mt-1 text-sm" style={{ color: "var(--lm-ink-2)" }}>
            {t("eSignatureHub.subtitle")}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {settings && (
            <Button variant="outline" onClick={() => setShowSettings(!showSettings)}>
              <Settings className="h-4 w-4 mr-2" />
              {t("eSignatureHub.settings.button")}
              {settings.provider === "yousign" && !settings.key_configured && (
                <span className="ml-2 w-2 h-2 rounded-full inline-block" style={{ backgroundColor: "#f59e0b" }} />
              )}
            </Button>
          )}
          <Button onClick={() => setShowForm(!showForm)}>
            <FileSignature className="h-4 w-4 mr-2" />
            {t("eSignatureHub.newRequest")}
          </Button>
        </div>
      </div>

      {/* Per-tenant Yousign configuration */}
      {showSettings && settings && (
        <div className="rounded-xl p-6 space-y-4"
          style={{ backgroundColor: "var(--lm-s1)", border: "1px solid var(--lm-border)" }}>
          <div className="flex items-center justify-between">
            <h4 className="font-semibold flex items-center gap-2" style={{ color: "var(--lm-ink)" }}>
              <ShieldCheck className="h-4 w-4" style={{ color: "#0ea5e9" }} />
              {t("eSignatureHub.settings.title")}
            </h4>
          </div>

          {/* Provider choice */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {[
              { id: "internal", title: t("eSignatureHub.settings.providerInternal"), desc: t("eSignatureHub.settings.providerInternalDesc") },
              { id: "yousign", title: t("eSignatureHub.settings.providerYousign"), desc: t("eSignatureHub.settings.providerYousignDesc") },
            ].map((p) => (
              <button key={p.id} type="button" onClick={() => setSettingsForm({ ...settingsForm, provider: p.id })}
                className="text-left rounded-lg p-4 transition-colors"
                style={{ border: settingsForm.provider === p.id ? "2px solid #0ea5e9" : "1px solid var(--lm-border)",
                  backgroundColor: settingsForm.provider === p.id ? "rgba(14,165,233,0.06)" : "var(--lm-canvas)" }}>
                <div className="flex items-center gap-2">
                  <span className="w-3.5 h-3.5 rounded-full inline-block"
                    style={{ border: "2px solid #0ea5e9", backgroundColor: settingsForm.provider === p.id ? "#0ea5e9" : "transparent" }} />
                  <span className="font-medium text-sm" style={{ color: "var(--lm-ink)" }}>{p.title}</span>
                </div>
                <p className="text-xs mt-1.5" style={{ color: "var(--lm-ink-2)" }}>{p.desc}</p>
              </button>
            ))}
          </div>

          {/* Yousign-only fields */}
          {settingsForm.provider === "yousign" && (
            <>
              <div className="flex items-center justify-end">
                <span className="text-xs px-2 py-0.5 rounded-full"
                  style={settings.key_configured
                    ? { backgroundColor: "rgba(34,197,94,0.12)", color: "#22c55e" }
                    : { backgroundColor: "rgba(245,158,11,0.12)", color: "#f59e0b" }}>
                  {settings.key_configured ? t("eSignatureHub.settings.keyConfigured") : t("eSignatureHub.settings.keyMissing")}
                </span>
              </div>
              <p className="text-xs" style={{ color: "var(--lm-ink-2)" }}>{t("eSignatureHub.settings.hint")}</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="md:col-span-2 space-y-1">
                  <label className="text-xs font-medium" style={{ color: "var(--lm-ink-2)" }}>{t("eSignatureHub.settings.apiKey")}</label>
                  <Input type="password" autoComplete="new-password"
                    placeholder={settings.key_configured ? t("eSignatureHub.settings.apiKeyConfiguredPlaceholder") : t("eSignatureHub.settings.apiKeyPlaceholder")}
                    value={settingsForm.api_key}
                    onChange={(e) => setSettingsForm({ ...settingsForm, api_key: e.target.value })} />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-medium" style={{ color: "var(--lm-ink-2)" }}>{t("eSignatureHub.settings.environment")}</label>
                  <select value={settingsForm.environment}
                    onChange={(e) => setSettingsForm({ ...settingsForm, environment: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg text-sm"
                    style={{ backgroundColor: "var(--lm-canvas)", border: "1px solid var(--lm-border)", color: "var(--lm-ink)" }}>
                    <option value="sandbox">{t("eSignatureHub.settings.sandbox")}</option>
                    <option value="production">{t("eSignatureHub.settings.production")}</option>
                  </select>
                </div>
              </div>
            </>
          )}

          <div className="flex items-center gap-2">
            <Button onClick={saveSettings} disabled={savingSettings}>
              {savingSettings ? t("eSignatureHub.settings.saving") : t("eSignatureHub.settings.save")}
            </Button>
            {settingsForm.provider === "yousign" && (
              <Button variant="outline" onClick={testSettings} disabled={testingSettings || !settings.key_configured}>
                {testingSettings ? t("eSignatureHub.settings.testing") : t("eSignatureHub.settings.test")}
              </Button>
            )}
          </div>
        </div>
      )}

      {/* KPI row */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: t("eSignatureHub.kpis.total"), value: stats.total, color: "#0ea5e9" },
          { label: t("eSignatureHub.kpis.pending"), value: stats.pending, color: "#f59e0b" },
          { label: t("eSignatureHub.kpis.signed"), value: stats.done, color: "#22c55e" },
        ].map((kpi) => (
          <div key={kpi.label} className="rounded-xl p-4"
            style={{ backgroundColor: "var(--lm-s1)", border: "1px solid var(--lm-border)" }}>
            <p className="text-xs font-medium" style={{ color: "var(--lm-ink-2)" }}>{kpi.label}</p>
            <p className="text-2xl font-bold mt-1" style={{ color: kpi.color }}>{kpi.value}</p>
          </div>
        ))}
      </div>

      {/* Send form */}
      {showForm && (
        <div className="rounded-xl p-6 space-y-5"
          style={{ backgroundColor: "var(--lm-s1)", border: "1px solid #0ea5e9" }}>
          <h4 className="font-semibold" style={{ color: "var(--lm-ink)" }}>{t("eSignatureHub.form.title")}</h4>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2 space-y-1">
              <label className="text-xs font-medium" style={{ color: "var(--lm-ink-2)" }}>{t("eSignatureHub.form.documentTitle")}</label>
              <Input placeholder={t("eSignatureHub.form.documentTitlePlaceholder")} value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })} />
            </div>
            <div className="md:col-span-2 space-y-1">
              <label className="text-xs font-medium" style={{ color: "var(--lm-ink-2)" }}>{t("eSignatureHub.form.message")}</label>
              <Input placeholder={t("eSignatureHub.form.messagePlaceholder")} value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })} />
            </div>
            <div className="md:col-span-2 space-y-1">
              <label className="text-xs font-medium" style={{ color: "var(--lm-ink-2)" }}>{t("eSignatureHub.form.pdf")}</label>
              <div className="flex items-center gap-3">
                <label className="cursor-pointer flex items-center gap-2 px-3 py-2 rounded-lg text-sm border transition-colors"
                  style={{ borderColor: form.pdf_base64 ? "#22c55e" : "var(--lm-border)", color: "var(--lm-ink-2)",
                    backgroundColor: "var(--lm-canvas)" }}>
                  <input type="file" accept=".pdf" className="hidden" onChange={handleFileChange} />
                  {form.pdf_base64
                    ? <><CheckCircle2 className="h-4 w-4" style={{ color: "#22c55e" }} />{form.document_filename}</>
                    : <><Plus className="h-4 w-4" />{t("eSignatureHub.form.selectPdf")}</>}
                </label>
              </div>
            </div>
          </div>

          {/* Signers */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <label className="text-xs font-semibold" style={{ color: "var(--lm-ink-2)" }}>
                {t("eSignatureHub.form.signers", { count: form.signers.length })}
              </label>
              <button onClick={addSigner} className="text-xs flex items-center gap-1 px-2 py-1 rounded-md"
                style={{ color: "#0ea5e9", backgroundColor: "rgba(14,165,233,0.08)" }}>
                <Plus className="h-3 w-3" /> {t("eSignatureHub.form.addSigner")}
              </button>
            </div>
            {form.signers.map((s, idx) => (
              <div key={idx} className="p-3 rounded-lg space-y-2"
                style={{ backgroundColor: "var(--lm-canvas)", border: "1px solid var(--lm-border)" }}>
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium flex items-center gap-1" style={{ color: "var(--lm-ink-2)" }}>
                    <User className="h-3 w-3" /> {t("eSignatureHub.form.signerNumber", { number: idx + 1 })}
                  </span>
                  {form.signers.length > 1 && (
                    <button onClick={() => removeSigner(idx)} style={{ color: "#ef4444" }}>
                      <X className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Input placeholder={t("eSignatureHub.form.firstName")} value={s.first_name}
                    onChange={(e) => updateSigner(idx, "first_name", e.target.value)} />
                  <Input placeholder={t("eSignatureHub.form.lastName")} value={s.last_name}
                    onChange={(e) => updateSigner(idx, "last_name", e.target.value)} />
                  <Input placeholder={t("eSignatureHub.form.email")} type="email" value={s.email}
                    onChange={(e) => updateSigner(idx, "email", e.target.value)} />
                  <Input placeholder={t("eSignatureHub.form.phone")} value={s.phone_number}
                    onChange={(e) => updateSigner(idx, "phone_number", e.target.value)} />
                </div>
              </div>
            ))}
          </div>

          <div className="flex gap-2 justify-end">
            <Button variant="outline" size="sm" onClick={() => setShowForm(false)}>{t("eSignatureHub.common.cancel")}</Button>
            <Button size="sm" onClick={handleSend} disabled={submitting}>
              <Send className="h-3.5 w-3.5 mr-1.5" />
              {submitting ? t("eSignatureHub.form.sending") : t("eSignatureHub.form.send")}
            </Button>
          </div>
        </div>
      )}

      {/* Requests list */}
      {loading ? (
        <div className="flex justify-center py-8">
          <div className="animate-spin h-6 w-6 rounded-full border-b-2" style={{ borderColor: "#0ea5e9" }} />
        </div>
      ) : requests.length === 0 ? (
        <div className="rounded-xl p-12 text-center"
          style={{ backgroundColor: "var(--lm-s1)", border: "1px dashed var(--lm-border)" }}>
          <FileSignature className="h-10 w-10 mx-auto mb-3" style={{ color: "var(--lm-ink-3)" }} />
          <p className="font-medium" style={{ color: "var(--lm-ink)" }}>{t("eSignatureHub.empty.title")}</p>
          <p className="text-sm mt-1" style={{ color: "var(--lm-ink-2)" }}>
            {t("eSignatureHub.empty.hint")}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {requests.map((req) => (
            <RequestCard
              key={req.id}
              req={req}
              expanded={expandedId === req.id}
              onToggle={() => setExpandedId(expandedId === req.id ? null : req.id)}
              onCancel={() => handleCancel(req.id)}
              onDownload={() => handleDownload(req)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function RequestCard({ req, expanded, onToggle, onCancel, onDownload }) {
  const { t, i18n } = useTranslation();
  const cfg = STATUS_CFG[req.status] || STATUS_CFG.draft;
  const StatusIcon = cfg.icon;
  const signers = req.signers || [];
  const signedCount = signers.filter((s) => s.signed_at).length;
  const isInternal = req.provider === "internal";
  const [links, setLinks] = useState({}); // email -> url

  useEffect(() => {
    if (expanded && isInternal && req.status === "pending") {
      api.get(`v1/esignature/${req.id}/signing-links`)
        .then((r) => setLinks(Object.fromEntries((r.data || []).filter((l) => l.url).map((l) => [l.email, l.url]))))
        .catch(() => {});
    }
  }, [expanded, isInternal, req.id, req.status]);

  const copyLink = async (email) => {
    const url = links[email];
    if (!url) return;
    try { await navigator.clipboard.writeText(url); toast.success(t("eSignatureHub.card.linkCopied")); }
    catch { window.prompt(t("eSignatureHub.card.copyManual"), url); }
  };

  const resend = async (email) => {
    try { await api.post(`v1/esignature/${req.id}/resend`, { email }); toast.success(t("eSignatureHub.card.emailResent")); }
    catch (e) { toast.error(e?.response?.data?.detail || t("eSignatureHub.toasts.sendError")); }
  };

  return (
    <div className="rounded-xl overflow-hidden"
      style={{ backgroundColor: "var(--lm-s1)", border: "1px solid var(--lm-border)" }}>
      <div className="flex items-center justify-between px-4 py-3.5">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <StatusIcon className="h-4 w-4 flex-shrink-0" style={{ color: cfg.color }} />
          <div className="min-w-0">
            <p className="text-sm font-medium truncate" style={{ color: "var(--lm-ink)" }}>{req.title}</p>
            <p className="text-xs" style={{ color: "var(--lm-ink-2)" }}>
              {req.document_filename} · {t("eSignatureHub.card.signerCount", { count: signers.length })}
              {req.status === "pending" && ` · ${t("eSignatureHub.card.signedCount", { signed: signedCount, total: signers.length })}`}
              {req.created_by_name && ` · ${req.created_by_name}`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 ml-3">
          <span className="px-2 py-0.5 rounded-full text-xs font-medium"
            style={{ backgroundColor: cfg.bg, color: cfg.color }}>
            {statusLabel(req.status, t)}
          </span>
          {req.status === "done" && (
            <button onClick={onDownload} title={t("eSignatureHub.card.downloadSigned")}
              className="p-1.5 rounded-md hover:opacity-80"
              style={{ backgroundColor: "rgba(34,197,94,0.12)", color: "#22c55e" }}>
              <Download className="h-3.5 w-3.5" />
            </button>
          )}
          {(req.status === "pending" || req.status === "draft") && (
            <button onClick={onCancel} title={t("eSignatureHub.common.cancel")}
              className="p-1.5 rounded-md hover:opacity-80"
              style={{ backgroundColor: "rgba(239,68,68,0.12)", color: "#ef4444" }}>
              <XCircle className="h-3.5 w-3.5" />
            </button>
          )}
          <button onClick={onToggle} className="p-1.5 rounded-md text-xs"
            style={{ color: "var(--lm-ink-3)" }}>
            {expanded ? "▲" : "▼"}
          </button>
        </div>
      </div>

      {expanded && (
        <div className="px-4 pb-4 space-y-3" style={{ borderTop: "1px solid var(--lm-border)" }}>
          {/* Signers */}
          <div className="mt-3 space-y-1.5">
            <p className="text-xs font-semibold" style={{ color: "var(--lm-ink-2)" }}>{t("eSignatureHub.card.signers")}</p>
            {signers.map((s, i) => (
              <div key={i} className="flex items-center justify-between text-xs px-3 py-1.5 rounded-lg"
                style={{ backgroundColor: "var(--lm-canvas)", border: "1px solid var(--lm-border)" }}>
                <span style={{ color: "var(--lm-ink)" }}>{s.first_name} {s.last_name} — {s.email}</span>
                <div className="flex items-center gap-2">
                  {isInternal && req.status === "pending" && !s.signed_at && (
                    <>
                      <button onClick={() => copyLink(s.email)} disabled={!links[s.email]}
                        title={t("eSignatureHub.card.copyLink")}
                        className="p-1 rounded hover:opacity-80" style={{ color: "#0ea5e9" }}>
                        <Link2 className="h-3.5 w-3.5" />
                      </button>
                      <button onClick={() => resend(s.email)} title={t("eSignatureHub.card.resendEmail")}
                        className="p-1 rounded hover:opacity-80" style={{ color: "var(--lm-ink-2)" }}>
                        <Mail className="h-3.5 w-3.5" />
                      </button>
                    </>
                  )}
                  {s.signed_at
                    ? <span style={{ color: "#22c55e" }}>✓ {new Date(s.signed_at).toLocaleDateString(i18n.language)}</span>
                    : <span style={{ color: "var(--lm-ink-3)" }}>{t("eSignatureHub.status.pending")}</span>}
                </div>
              </div>
            ))}
          </div>

          {/* Audit trail */}
          {(req.audit_trail || []).length > 0 && (
            <div className="space-y-1">
              <p className="text-xs font-semibold" style={{ color: "var(--lm-ink-2)" }}>{t("eSignatureHub.card.history")}</p>
              {[...(req.audit_trail)].reverse().slice(0, 6).map((ev, i) => (
                <div key={i} className="flex items-start gap-2 text-xs" style={{ color: "var(--lm-ink-2)" }}>
                  <span className="font-mono" style={{ color: "var(--lm-ink-3)", flexShrink: 0 }}>
                    {new Date(ev.ts).toLocaleString(i18n.language, { dateStyle: "short", timeStyle: "short" })}
                  </span>
                  <span style={{ color: "var(--lm-ink)" }}>{ev.event}</span>
                  {ev.detail && <span style={{ color: "var(--lm-ink-3)" }}>— {ev.detail}</span>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
